import { useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { orgUnitsApi } from '../../../human-resources/organization/organizationApi';
import type { OrgUnitOption } from '../../../human-resources/organization/organizationTypes';
import { storageFacilitiesApi } from '../inventoryApi';
import type { StorageFacilityListItem, StorageFacilityOption } from '../inventoryTypes';
import { sampleStorageFacility } from '../utils/sampleInventory';

/**
 * Create or edit one storage facility.
 *
 * Re-parenting is an ordinary field rather than its own operation, unlike moving an org unit
 * between tiers: the number of rows written is one, because a store chain carries no rank for a
 * descendant to be re-derived against. The children come along by staying where they are.
 *
 * <b>There is no Code field, on either operation.</b> A facility's code is `FAC-{yy}{NNN}`,
 * allocated by the server and permanent — a position is assigned, never asked for. On an edit it
 * is stated in the TITLE, which is where `<EditTrainingModal>` states its own record number:
 * the reader needs to know which store they are correcting, and a read-only row inside the form
 * would be a control that collects nothing.
 */
export function StorageFacilityModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: StorageFacilityListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Another module's records, so its own key — and hidden rather than disabled without it.
  const canReadOrgUnits = usePermission('org-units.read');

  const [name, setName] = useState(editing?.name ?? '');
  const [facilityType, setFacilityType] = useState(editing?.facilityType ?? '');
  const [parentId, setParentId] = useState(editing?.parentFacilityId ?? '');
  const [orgUnitId, setOrgUnitId] = useState(editing?.orgUnitId ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnitOption[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [loadingOrgUnits, setLoadingOrgUnits] = useState(false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadingFacilities(true);

    storageFacilitiesApi
      .options(controller.signal)
      .then(setFacilities)
      .catch(() => undefined)
      .finally(() => setLoadingFacilities(false));

    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    if (!open || !canReadOrgUnits) return;

    const controller = new AbortController();
    setLoadingOrgUnits(true);

    orgUnitsApi
      .options(undefined, controller.signal)
      .then(setOrgUnits)
      .catch(() => undefined)
      .finally(() => setLoadingOrgUnits(false));

    return () => controller.abort();
  }, [open, canReadOrgUnits]);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      facilityType: facilityType.trim() || null,
      parentFacilityId: parentId === '' ? null : parentId,
      orgUnitId: orgUnitId === '' ? null : orgUnitId,
      notes: notes.trim() || null,
    };

    try {
      if (editing) await storageFacilitiesApi.update(editing.id, request);
      else await storageFacilitiesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the facility.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'storage-facility' });
    } finally {
      setSaving(false);
    }
  }

  /*
    SELF is excluded from the parent list, because a store inside itself is the one cycle a
    reader could pick by accident from a flat list of names.

    Its DESCENDANTS are not excluded, and that is deliberate rather than an omission: the
    options feed is flat, so filtering a subtree would mean re-deriving the hierarchy on the
    client — two places deciding one shape. The server refuses the move and names
    `parentFacilityId`, so the rejection lands on the control that is wrong.
  */
  const parentOptions = facilities
    .filter((f) => f.id !== editing?.id)
    .map((f) => ({ value: f.id, label: f.path, keywords: f.code }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.code}` : 'New facility'}
      size="lg"
      id="storage-facility-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleStorageFacility();
          setName(sample.name);
          setFacilityType(sample.facilityType);
          setNotes(sample.notes);
        },
        disabled: saving,
      }}
      confirm={{
        id: 'storage-facility-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="storage-facility-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="storage-facility-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          ONE grid for the four fields that flow together, rather than two of two. With the
          department drawn it fills two rows exactly; without it the parent picker ends the run
          on its own line, which reads as the end of a run rather than as a section with one
          item in it — the failure a grid of its own would produce.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="storage-facility-name"
            label="Name"
            required
            description="What the store is called. Held once among the stores inside the same parent — two warehouses may each hold a Shelf A."
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={150}
            disabled={saving}
            error={error?.fieldError('name')}
          />

          {/*
            Free text rather than a catalog, and the line is drawn deliberately: nothing is ever
            totalled by facility type, so two spellings cost a moment's untidiness rather than a
            wrong number. The unit of measure is on the other side of that line.
          */}
          <TextField
            id="storage-facility-facility-type"
            label="Type"
            description="What kind of place it is, e.g. Warehouse or Cold Storage. Free text — the list groups whatever is typed."
            value={facilityType}
            onChange={(e) => setFacilityType(e.target.value)}
            maxLength={60}
            disabled={saving}
            error={error?.fieldError('facilityType')}
          />

          <Select
            id="storage-facility-parent-facility-id"
            label="Sits inside"
            description="Leave empty for a top-level store. A store cannot sit inside itself or one of its own shelves."
            placeholder="Top level"
            value={parentId}
            onChange={setParentId}
            options={parentOptions}
            remote
            loading={loadingFacilities}
            disabled={saving}
            error={error?.fieldError('parentFacilityId')}
          />

          {canReadOrgUnits ? (
            <Select
              id="storage-facility-org-unit-id"
              label="Department"
              description="The unit that owns this store, when one does — an engineering store, a dietary store. The name is read live, so renaming the unit renames it here."
              placeholder="No department"
              value={orgUnitId}
              onChange={setOrgUnitId}
              options={orgUnits.map((u) => ({
                value: u.id,
                label: u.name,
                sublabel: u.orgUnitTypeName,
              }))}
              remote
              loading={loadingOrgUnits}
              disabled={saving}
              error={error?.fieldError('orgUnitId')}
            />
          ) : null}
        </div>

        <TextField
          id="storage-facility-notes"
          label="Notes"
          description="Optional."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('notes')}
        />
      </div>
    </Modal>
  );
}
